import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { lazily } from 'react-lazily';
import { v4 as uuid } from 'uuid';
import { useSearchParams } from 'react-router-dom';

import type { GlobalFilter } from '@hotjar/global-filters';
import {
  convertFromFilterTypeToQueryFormat,
  getDefaultDateRange,
  readDateRangeFromUrl,
  readFilterSeriesFromUrl,
  convertFromNewUiToQueryFormat,
} from '@hotjar/global-filters';
import { useCurrentSite, useCurrentSiteId } from '@hotjar/site';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { PageLoader } from '@hotjar/webapp/components/PageLoader/PageLoader';
import { ErrorBoundaryForPages } from '@hotjar/error-handler';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import type { AdditionalTrackingData, TrendQuery } from '@hotjar/dashboard-quantz';
import { ChartType, readChartTypeFromUrl, getSeriesLabel } from '@hotjar/dashboard-quantz';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';

import { noop } from '@hotjar/webapp/lib/function';
import { isAllowed } from '@hotjar/webapp/domains/common/feature';
import { segment as trackSegment } from '@hotjar/tracking';

import { useAccountTrialInfo } from '@hotjar/account';
import { getColorByIndex } from '@hotjar/charts';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';
import type { KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import { useGate } from '@hotjar/allowances';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import { isSiteOnScaleOrScaleTrial } from '@hotjar/recordings/src/utils/isSiteOnScaleOrScaleTrial';
import { isOnActiveOrEligibleTrial } from '@hotjar/subscription-management';
import { TrafficAndCoverageBanner } from '@hotjar/webapp/components/TrafficAndCoverageBanner/TrafficAndCoverageBanner';
import { TRAFFIC_UPSELL_BANNER } from '@hotjar/upsell';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { useTranslation } from '@hotjar/translations';
import {
  getDateRangeLabel,
  getAxisResolution,
  trackDashboardUpdated,
} from '../AsyncDashboardQuantzPage/trackMethods';
import { TrendsDateFilterChain } from './TrendsDateFilterChain';
import { MultiFilterChain } from '../FunnelsPage/QuerySection/MultiFilterChain/MultiFilterChain';
import { getAppliedFiltersList } from '../FunnelsPage/QuerySection/MultiFilterChain/utils';
import { SplashScreen } from './SplashScreen';
import { ScaleTrialGuideCard } from './GuideCard/ScaleTrialGuideCard';

// We use lazily lib to be able to import component without a default export.
const { TrendsPage } = lazily(() => import('@hotjar/dashboard-quantz'));

const getDefaultTrendQuery = (): TrendQuery => {
  return {
    dateRange: getDefaultDateRange(),
    filterChainSeries: [
      {
        key: uuid(),
        filtersInUiFormat: [],
        color: getColorByIndex(0),
        label: getSeriesLabel([]),
      },
    ],
    chartType: ChartType.timeline,
  };
};

export const AsyncTrendsPage = (): JSX.Element => {
  const { t } = useTranslation('dashboard-quantz');
  usePageTitle(`${t('TrendsPage.title')} - Hotjar`);
  const { data: site } = useCurrentSite();
  const currentSiteId = useCurrentSiteId();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setSearchParams] = useSearchParams();
  const { click, viewElement, getPermissionLevelForSite } = useTrack();
  const { isLoading, coveragePercentage } = useTrafficCoverage();
  const isTrendFeatureAllowed = useGate('analytics.trends_page', {
    id: currentSiteId,
    type: 'site',
  });

  /**
   * TODO: Refactor logic in RECS-1380 to be self-contained in hook
   */
  const trialInfo = useAccountTrialInfo();
  const isNewScaleEnabled = useFeatureFlag('billing_new_scale');
  const isSiteInScaleTrialCohort =
    useFeatureFlag('scale_trial_cohort') && site?.effectivePlan === 'business';
  const isScaleTrial =
    (isNewScaleEnabled && isSiteInScaleTrialCohort) ||
    (!isNewScaleEnabled &&
      !isOnActiveOrEligibleTrial(trialInfo) &&
      site?.effectivePlan === 'business');
  const isScaleOrScaleTrial =
    site &&
    (trialInfo || undefined) &&
    isSiteOnScaleOrScaleTrial({ ...site, trial: { active: isOnActiveOrEligibleTrial(trialInfo) } });

  const hasDashboardUpdatePermission = usePermission('dashboard-update');
  const [currentTrendQuery, setCurrentTrendQuery] = useState<TrendQuery>(getDefaultTrendQuery());
  const isKaboomExceptionActive = useFeatureFlag('activateKaboomException');
  const [urlHasBeenRead, setUrlHasBeenRead] = useState<boolean>(false);

  const setFilterSeriesAndDateRangeAndUrl = useCallback(
    (newFilterChainSeriesList: TrendQuery) => {
      setCurrentTrendQuery(newFilterChainSeriesList);

      const keyValuePairList: [string, string][] = [];
      // Global filter part
      const globalFilterString = JSON.stringify(
        newFilterChainSeriesList.filterChainSeries.map((item) =>
          convertFromFilterTypeToQueryFormat(item.filtersInUiFormat),
        ),
      );
      keyValuePairList.push(['filter-series', globalFilterString]);

      // dateRange part
      const dateRangeString = JSON.stringify(
        convertFromNewUiToQueryFormat({
          dateRange: newFilterChainSeriesList.dateRange,
          filterList: [],
        }),
      );
      keyValuePairList.push(['date', dateRangeString]);

      // chart type part
      keyValuePairList.push(['chart', newFilterChainSeriesList.chartType]);

      setSearchParams(keyValuePairList);
    },
    [setCurrentTrendQuery, setSearchParams],
  );

  const onDateFiltersChange = useCallback(
    (newDateRange: GlobalFilter) => {
      setFilterSeriesAndDateRangeAndUrl({
        ...currentTrendQuery,
        dateRange: newDateRange.dateRange ?? currentTrendQuery.dateRange,
      });
    },
    [currentTrendQuery, setFilterSeriesAndDateRangeAndUrl],
  );

  const setFiltersFromUrl = useCallback(() => {
    let trendsQueryFromUrl: TrendQuery;
    try {
      const dateRangeResult = readDateRangeFromUrl();
      const globalFilterSeriesResult = readFilterSeriesFromUrl();
      if (globalFilterSeriesResult.filterSeriesInUiFormat.length === 0) {
        trendsQueryFromUrl = getDefaultTrendQuery();
      } else {
        trendsQueryFromUrl = {
          dateRange: dateRangeResult.dateRange,
          filterChainSeries: globalFilterSeriesResult.filterSeriesInUiFormat.map((item, index) => ({
            key: uuid(),
            filtersInUiFormat: item,
            color: getColorByIndex(index),
            label: getSeriesLabel(item),
          })),
          chartType: readChartTypeFromUrl(ChartType.timeline),
        };
      }
    } catch {
      trendsQueryFromUrl = getDefaultTrendQuery();
    }
    setCurrentTrendQuery(trendsQueryFromUrl);
    setUrlHasBeenRead(true);
  }, []);

  useEffect(() => {
    setFiltersFromUrl();
    // We just want to run it once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openUpsellModal = useCallback(
    (referrer: KnownReferrer, additionalTrackingData?: AdditionalTrackingData) => {
      if (site != null) {
        upsell({
          site,
          referrer,
          additionalTrackingData: {
            state_event_was_triggered_on: 'trends',
            ...additionalTrackingData,
          },
        }).then(noop, noop);
      }
    },
    [site],
  );

  const segmentData = useMemo(
    () => ({
      account_id: site?.account.id,
      site_id: site?.id,
      organization_id: site?.organization.id,
      permission_level: site ? getPermissionLevelForSite(site) || '' : '',
      shown_traffic_coverage: coveragePercentage,
      state_event_was_triggered_on: 'trends',
    }),
    [coveragePercentage, getPermissionLevelForSite, site],
  );

  const trackClick = useCallback(
    (eventName: string, uiPosition: string, additionalData: Record<string, string>) =>
      click(eventName, uiPosition, {
        ...segmentData,
        ...additionalData,
      }),
    [click, segmentData],
  );

  const trackViewElement = useCallback(
    (eventName: string, uiPosition: string, additionalData: Record<string, string>) =>
      viewElement(eventName, uiPosition, {
        ...segmentData,
        ...additionalData,
      }),
    [segmentData, viewElement],
  );

  if (!site || isLoading || !urlHasBeenRead) {
    return <PageLoader />;
  }

  if (isKaboomExceptionActive === true) {
    throw new Error('KABOOM');
  }

  const trackTrendViewed = (numberMatchingSessions: number, chartType: ChartType) => {
    const appliedFiltersList = currentTrendQuery.filterChainSeries
      .map((series) => getAppliedFiltersList(series.filtersInUiFormat))
      .flat();
    trackSegment.track('Trend Viewed', {
      ...(segmentData as trackSegment.SegmentTrackData),
      trend_id: '',
      trend_title: '',
      trend_description: '',
      trend_save_state: 'unsaved',
      trend_added_to_dashboard: false,
      date_range: getDateRangeLabel(currentTrendQuery.dateRange),
      number_matching_sessions: numberMatchingSessions,
      number_filter_groups_applied: currentTrendQuery.filterChainSeries.length,
      trend_chart_type: chartType,
      trend_chart_calculation: 'total',
      trend_x_axis_resolution_selection: 'auto',
      trend_x_axis_resolution_shown: getAxisResolution(currentTrendQuery.dateRange),
      trend_data_queries:
        appliedFiltersList.length === 0 ? ['all_sessions-no_filters_applied'] : appliedFiltersList,
    });
  };

  // site.id === 14 condition makes testing Trends on integration environment easier
  // enabling the Scale plan is not straightforward there
  if (!isScaleTrial && !isTrendFeatureAllowed && site.id !== 14) {
    return (
      <SplashScreen
        trackClick={(eventName, additionalData) => {
          // eslint-disable-next-line camelcase
          const { ui_position, ...rest } = additionalData;
          trackClick(eventName, ui_position, rest as Record<string, string>);
        }}
        trackViewElement={(eventName, additionalData) => {
          // eslint-disable-next-line camelcase
          const { ui_position, ...rest } = additionalData;
          trackViewElement(eventName, ui_position, rest as Record<string, string>);
        }}
      />
    );
  }

  const multiFilterChainComponent = (
    <MultiFilterChain
      filterChainSeriesList={currentTrendQuery.filterChainSeries}
      dateRange={currentTrendQuery.dateRange}
      onFilterChainSeriesUpdate={(newFilterChainSeriesList, newDateRange) => {
        setFilterSeriesAndDateRangeAndUrl({
          ...currentTrendQuery,
          dateRange: newDateRange ?? currentTrendQuery.dateRange,
          filterChainSeries: newFilterChainSeriesList,
        });
      }}
      onUpsellRequest={openUpsellModal}
      trackClick={(eventName, additionalData) => {
        // eslint-disable-next-line camelcase
        const { ui_position, ...rest } = additionalData;
        trackClick(eventName, ui_position, rest as Record<string, string>);
      }}
      contextOfUse={{
        productType: ProductType.Analytics,
        page: 'trends',
        toolName: 'Trends',
      }}
    />
  );

  const dateFilterChainComponent = (
    <TrendsDateFilterChain
      currentFiltersInUiFormat={{
        dateRange: currentTrendQuery.dateRange,
        filterList: currentTrendQuery.filterChainSeries
          .map((item) => item.filtersInUiFormat)
          .flat(),
      }}
      onFiltersChange={onDateFiltersChange}
      onUpsellRequest={openUpsellModal}
    />
  );

  const bannerComponent = (
    <TrafficAndCoverageBanner
      type={TRAFFIC_UPSELL_BANNER.TRENDS}
      track={{ page: 'trends', trackViewElement, trackClick }}
    />
  );

  return (
    <ErrorBoundaryForPages squadSentryTag="product-foundations" featureName="trends">
      <Suspense fallback={<PageLoader />}>
        <TrendsPage
          siteId={site.id}
          currentSite={site}
          trendQuery={currentTrendQuery}
          guideCard={isScaleTrial && <ScaleTrialGuideCard />}
          filterChainComponent={multiFilterChainComponent}
          dateFilterChainComponent={dateFilterChainComponent}
          bannerComponent={bannerComponent}
          setChartType={(newChartType) => {
            setFilterSeriesAndDateRangeAndUrl({
              ...currentTrendQuery,
              chartType: newChartType,
            });
          }}
          openUpsellModal={
            (referrer, additionalTrackingData) =>
              openUpsellModal(referrer as KnownReferrer, additionalTrackingData) // FIXME: remove type assertion
          }
          trackClick={(eventName, additionalData) => {
            // eslint-disable-next-line camelcase
            const { ui_position, ...rest } = additionalData;
            trackClick(eventName, ui_position, rest as Record<string, string>);
          }}
          trackViewElement={(eventName, additionalData) => {
            // eslint-disable-next-line camelcase
            const { ui_position, ...rest } = additionalData;
            trackViewElement(eventName, ui_position, rest as Record<string, string>);
          }}
          trackTrendViewed={trackTrendViewed}
          trackDashboardUpdated={(config, appliedActions) =>
            trackDashboardUpdated({ site }, config, appliedActions)
          }
          isAllowed={isAllowed}
          isScaleTrial={isScaleTrial}
          isScaleOrScaleTrial={isScaleOrScaleTrial}
          hasDashboardUpdatePermission={hasDashboardUpdatePermission}
        />
      </Suspense>
    </ErrorBoundaryForPages>
  );
};
